import { ColumnPin, View } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";

type ViewPin = ColumnPin | "center";

/**
 * Reconcile a cached view with the current view config so stale cache entries
 * (e.g. column ids that have been removed or renamed in code) don't break the
 * UI. Drops cached column ids that aren't in the config, re-adds default-view
 * columns the config marks immutable (they cannot be deselected, so a stale
 * cache is the only way one goes missing), and falls back to the provided
 * default view when the result is empty. Pin overrides from
 * `defaultView` are always applied, and pinned columns are re-sorted to the
 * right so older caches that weren't aware of newly-pinned columns don't end
 * up rendering them in the middle of the table.
 */
export function mergeCachedView<T extends View>(
    cached: T | undefined,
    defaultView: T,
    viewConfig: ViewConfig<any, string, string>,
): T {
    if (!cached) {
        return defaultView;
    }

    const knownColumnIds = new Set(Object.keys(viewConfig.columns));
    const defaultPinById = new Map(
        defaultView.columns.map((c) => [c.id, c.options?.pin]),
    );

    const enforcedColumns = cached.columns
        .filter((col) => knownColumnIds.has(col.id))
        .map((col): View["columns"][number] => {
            if (!defaultPinById.has(col.id)) {
                return col;
            }
            const enforcedPin = defaultPinById.get(col.id);
            if (col.options?.pin === enforcedPin) {
                return col;
            }
            if (enforcedPin === undefined) {
                const { pin: _pin, ...otherOptions } = col.options ?? {};
                return { ...col, options: otherOptions };
            }
            return {
                ...col,
                options: { ...col.options, pin: enforcedPin },
            };
        });

    if (enforcedColumns.length === 0) {
        return defaultView;
    }

    const enforcedIds = new Set(enforcedColumns.map((col) => col.id));
    const missingImmutableColumns = defaultView.columns.filter(
        (col) =>
            !enforcedIds.has(col.id) && viewConfig.columns[col.id]?.immutable,
    );
    const reconciledColumns = [...enforcedColumns, ...missingImmutableColumns];

    const pinOrder: Record<ViewPin, number> = { left: 0, center: 1, right: 2 };
    const sortedColumns = reconciledColumns
        .map((col, index) => ({ col, index }))
        .toSorted((a, b) => {
            const aPin: ViewPin = a.col.options?.pin ?? "center";
            const bPin: ViewPin = b.col.options?.pin ?? "center";
            const pinDiff = pinOrder[aPin] - pinOrder[bPin];
            return pinDiff !== 0 ? pinDiff : a.index - b.index;
        })
        .map((entry) => entry.col);

    return { ...cached, columns: sortedColumns };
}

/**
 * Adds a column a cached view predates, ahead of the right-pinned columns so
 * it lands with the other data columns. Returns the view untouched when the
 * column is already there.
 */
export function ensureViewColumn<T extends View>(view: T, columnId: string): T {
    if (view.columns.some((column) => column.id === columnId)) {
        return view;
    }
    const firstRightPinned = view.columns.findIndex(
        (column) => column.options?.pin === "right",
    );
    const insertAt =
        firstRightPinned === -1 ? view.columns.length : firstRightPinned;
    return {
        ...view,
        columns: [
            ...view.columns.slice(0, insertAt),
            { id: columnId },
            ...view.columns.slice(insertAt),
        ],
    };
}
